// 设备常量服务 - 从服务端获取设备检测相关常量
import api from './api';

export interface TemplateOption {
  code: string;
  name: string;
  file_name: string;
}

// 设备常量类型定义
export interface DeviceConstants {
  device_types: Record<string, string>;
  client_types: Record<string, string>;
  os_types: Record<string, string>;
  browser_types: Record<string, string>;
  device_brands: Record<string, string>;
  network_types: Record<string, string>;
  risk_levels: Record<string, string>;
  proxy_types: Record<string, string>;
  language_codes: Record<string, string>;
  country_codes: Record<string, string>;
  bot_types: Record<string, string>;
  utm_sources: Record<string, string>;
  utm_mediums: Record<string, string>;
  utm_campaigns: Record<string, string>;
  utm_terms: Record<string, string>;
  utm_contents: Record<string, string>;
  mobile_apps: Record<string, string>;
  ad_platforms: Record<string, string>;
  templates?: TemplateOption[];
  ios_versions: Record<string, string>;
  android_versions: Record<string, string>;
  device_models: Record<string, string>;
  device_models_by_brand: Record<string, Record<string, string>>;
}

// 设备常量响应类型
export interface DeviceConstantsResponse {
  success: boolean;
  data: DeviceConstants;
}

const CACHE_KEY = 'device_constants_cache';
const CACHE_TIMESTAMP_KEY = 'device_constants_cache_timestamp';
const CACHE_DURATION = 30 * 60 * 1000; // 30分钟缓存

function normalizeDeviceConstants(data: DeviceConstants | null): DeviceConstants | null {
  if (!data) return data;

  const templates = Array.isArray(data.templates)
    ? data.templates
        .map((tpl) => ({
          code: tpl?.code ?? tpl?.file_name?.replace(/\.html$/i, '') ?? '',
          name: tpl?.name ?? tpl?.file_name ?? '自定义模板',
          file_name: tpl?.file_name ?? '',
        }))
        .filter((tpl) => tpl.file_name)
    : [];

  return {
    ...data,
    templates,
  };
}

// 从 localStorage 读取缓存
function getFromStorage(): { data: DeviceConstants | null; timestamp: number } {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
    
    if (cached && timestamp) {
      return {
        data: normalizeDeviceConstants(JSON.parse(cached)),
        timestamp: parseInt(timestamp, 10)
      };
    }
  } catch (error) {
    console.error('Error reading from localStorage:', error);
  }
  
  return { data: null, timestamp: 0 };
}

// 保存到 localStorage
function saveToStorage(data: DeviceConstants): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
}

// 清除 localStorage 缓存
function clearStorage(): void {
  try {
    localStorage.removeItem(CACHE_KEY);
    localStorage.removeItem(CACHE_TIMESTAMP_KEY);
  } catch (error) {
    console.error('Error clearing localStorage:', error);
  }
}

/**
 * 获取设备常量
 * @param forceRefresh 是否强制刷新缓存
 * @returns Promise<DeviceConstants>
 */
export async function getDeviceConstants(forceRefresh: boolean = false): Promise<DeviceConstants> {
  // 如果强制刷新，清除存储
  if (forceRefresh) {
    clearStorage();
  }
  
  // 检查 localStorage 缓存
  const { data: cachedData, timestamp } = getFromStorage();
  if (!forceRefresh && cachedData && Date.now() - timestamp < CACHE_DURATION) {
    console.log('Using device constants from localStorage cache');
    return cachedData;
  }

  try {
    const response = await api.post('/api/device/constants', {});
    
    if (response.data.success && response.data.data) {
      const normalized = normalizeDeviceConstants(response.data.data) as DeviceConstants;
      // 保存到 localStorage
      saveToStorage(normalized);
      
      console.log('Device constants fetched and cached');
      return normalized;
    } else {
      throw new Error('Failed to get device constants');
    }
  } catch (error) {
    console.error('Error fetching device constants:', error);
    
    // 如果 localStorage 缓存存在，返回缓存数据
    if (cachedData) {
      console.warn('Using cached device constants from localStorage due to API error');
      return cachedData;
    }
    
    // 不再提供默认值，直接抛出错误
    throw new Error('Device constants not available and no cache found');
  }
}

/**
 * 清除设备常量缓存
 */
export function clearDeviceConstantsCache(): void {
  clearStorage();
}

export async function getTemplateOptions(): Promise<Array<{ value: string; label: string; code: string }>> {
  const constants = await getDeviceConstants();
  return (constants.templates ?? []).map((tpl) => ({
    value: tpl.file_name,
    label: tpl.name,
    code: tpl.code,
  }));
}

/**
 * 获取设备类型选项
 */
export async function getDeviceTypeOptions() {
  const constants = await getDeviceConstants();
  return Object.entries(constants.device_types).map(([value, label]) => ({
    value,
    label
  }));
}

/**
 * 获取客户端类型选项
 */
export async function getClientTypeOptions() {
  const constants = await getDeviceConstants();
  return Object.entries(constants.client_types).map(([value, label]) => ({
    value,
    label
  }));
}

/**
 * 获取操作系统类型选项
 */
export async function getOSTypeOptions() {
  const constants = await getDeviceConstants();
  return Object.entries(constants.os_types).map(([value, label]) => ({
    value,
    label
  }));
}

/**
 * 获取浏览器类型选项
 */
export async function getBrowserTypeOptions() {
  const constants = await getDeviceConstants();
  return Object.entries(constants.browser_types).map(([value, label]) => ({
    value,
    label
  }));
}

/**
 * 获取设备品牌选项
 */
export async function getDeviceBrandOptions() {
  const constants = await getDeviceConstants();
  return Object.entries(constants.device_brands).map(([value, label]) => ({
    value,
    label
  }));
}

/**
 * 获取网络类型选项
 */
export async function getNetworkTypeOptions() {
  const constants = await getDeviceConstants();
  return Object.entries(constants.network_types).map(([value, label]) => ({
    value,
    label
  }));
}

/**
 * 获取风险等级选项
 */
export async function getRiskLevelOptions() {
  const constants = await getDeviceConstants();
  return Object.entries(constants.risk_levels).map(([value, label]) => ({
    value,
    label
  }));
}

/**
 * 获取代理类型选项
 */
export async function getProxyTypeOptions() {
  const constants = await getDeviceConstants();
  return Object.entries(constants.proxy_types).map(([value, label]) => ({
    value,
    label
  }));
}

/**
 * 获取语言代码选项
 */
export async function getLanguageCodeOptions() {
  const constants = await getDeviceConstants();
  return Object.entries(constants.language_codes).map(([value, label]) => ({
    value,
    label
  }));
}

/**
 * 获取爬虫类型选项
 */
export async function getBotTypeOptions() {
  const constants = await getDeviceConstants();
  return Object.entries(constants.bot_types).map(([value, label]) => ({
    value,
    label
  }));
}

/**
 * 获取设备类型显示文本
 */
export async function getDeviceTypeLabel(value: string): Promise<string> {
  const constants = await getDeviceConstants();
  return constants.device_types[value] || value;
}

/**
 * 获取客户端类型显示文本
 */
export async function getClientTypeLabel(value: string): Promise<string> {
  const constants = await getDeviceConstants();
  return constants.client_types[value] || value;
}

/**
 * 获取操作系统类型显示文本
 */
export async function getOSTypeLabel(value: string): Promise<string> {
  const constants = await getDeviceConstants();
  return constants.os_types[value] || value;
}

/**
 * 获取浏览器类型显示文本
 */
export async function getBrowserTypeLabel(value: string): Promise<string> {
  const constants = await getDeviceConstants();
  return constants.browser_types[value] || value;
}

/**
 * 获取设备品牌显示文本
 */
export async function getDeviceBrandLabel(value: string): Promise<string> {
  const constants = await getDeviceConstants();
  return constants.device_brands[value] || value;
}

/**
 * 获取网络类型显示文本
 */
export async function getNetworkTypeLabel(value: string): Promise<string> {
  const constants = await getDeviceConstants();
  return constants.network_types[value] || value;
}

/**
 * 获取风险等级显示文本
 */
export async function getRiskLevelLabel(value: string): Promise<string> {
  const constants = await getDeviceConstants();
  return constants.risk_levels[value] || value;
}

/**
 * 获取代理类型显示文本
 */
export async function getProxyTypeLabel(value: string): Promise<string> {
  const constants = await getDeviceConstants();
  return constants.proxy_types[value] || value;
}

/**
 * 获取语言代码显示文本
 */
export async function getLanguageCodeLabel(value: string): Promise<string> {
  const constants = await getDeviceConstants();
  return constants.language_codes[value] || value;
}

/**
 * 获取爬虫类型显示文本
 */
export async function getBotTypeLabel(value: string): Promise<string> {
  const constants = await getDeviceConstants();
  return constants.bot_types[value] || value;
}

/**
 * 获取移动应用选项
 */
export async function getMobileAppOptions() {
  const constants = await getDeviceConstants();
  return Object.entries(constants.mobile_apps).map(([value, label]) => ({
    value,
    label
  }));
}

/**
 * 获取移动应用显示文本
 */
export async function getMobileAppLabel(value: string): Promise<string> {
  const constants = await getDeviceConstants();
  return constants.mobile_apps[value] || value;
}
